import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { User, Building, Mail, Lock, Moon, Sun, Bell, Globe, Save, Bot, Key, CheckCircle, AlertCircle, Loader, Trash2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  nl: {
    title: 'Profiel Instellingen',
    loading: 'Profiel laden...',
    // Profile section
    profileInfo: 'Profiel Informatie',
    fullName: 'Volledige Naam',
    companyName: 'Bedrijfsnaam',
    email: 'E-mailadres',
    emailHint: 'E-mailadres kan niet gewijzigd worden',
    saveChanges: 'Wijzigingen Opslaan',
    saving: 'Opslaan...',
    profileSaved: 'Profiel bijgewerkt!',
    profileError: 'Fout bij opslaan profiel',
    // Preferences
    preferences: 'Voorkeuren',
    theme: 'Thema',
    themeHint: 'Kies je interface thema',
    light: 'Licht',
    dark: 'Donker',
    notifications: 'E-mail Notificaties',
    notificationsHint: 'Ontvang updates en meldingen',
    language: 'Standaardtaal',
    languageHint: 'Voorkeurstaal voor de interface',
    savePrefs: 'Voorkeuren Opslaan',
    prefsSaved: 'Voorkeuren opgeslagen!',
    prefsError: 'Fout bij opslaan voorkeuren',
    // Password
    changePassword: 'Wachtwoord Wijzigen',
    currentPassword: 'Huidig Wachtwoord',
    newPassword: 'Nieuw Wachtwoord',
    confirmPassword: 'Bevestig Nieuw Wachtwoord',
    changeBtn: 'Wachtwoord Wijzigen',
    changing: 'Wijzigen...',
    passwordChanged: 'Wachtwoord gewijzigd!',
    passwordMismatch: 'Nieuwe wachtwoorden komen niet overeen',
    // AI Settings
    aiTitle: 'AI Model Instellingen',
    aiDesc: 'Kies welk AI-model alle tools in het platform aanstuurt. Je kunt ons standaard Gemini model gebruiken of je eigen API sleutels koppelen.',
    aiProvider: 'AI Provider',
    noKeyWarning: 'Geen API sleutel geconfigureerd voor',
    addKeyLink: 'API Sleutel toevoegen',
    model: 'Model',
    testConnection: 'Verbinding Testen',
    testing: 'Testen...',
    saveAi: 'AI Instellingen Opslaan',
    aiSaved: 'AI instellingen opgeslagen!',
    aiError: 'Fout bij opslaan AI instellingen',
    connectionSuccess: 'Verbinding Succesvol!',
    connectionFailed: 'Verbinding Mislukt',
    // Delete
    deleteTitle: 'Account Verwijderen',
    deleteDesc: 'Hiermee wordt je account permanent verwijderd, inclusief alle bedrijfsdata, documenten, analyses en je AI-assistent Emma. Dit kan niet ongedaan worden gemaakt.',
    deleteBtn: 'Account Verwijderen',
    deleteConfirmTitle: 'Account Verwijderen?',
    deleteConfirmMsg: 'Weet je zeker dat je je account wilt verwijderen? <strong>Alle data</strong> inclusief je bedrijfsprofiel, documenten, analyses en je Agentic AI-assistent <strong>Emma</strong> worden permanent verwijderd. Dit kan niet ongedaan worden gemaakt.',
    deleteTypePrompt: 'Typ <strong>VERWIJDEREN</strong> om te bevestigen:',
    deleteWord: 'VERWIJDEREN',
    cancel: 'Annuleren',
    deleting: 'Verwijderen...',
    deleteForever: 'Definitief Verwijderen',
    deleteError: 'Er ging iets mis',
  },
  en: {
    title: 'Profile Settings',
    loading: 'Loading profile...',
    profileInfo: 'Profile Information',
    fullName: 'Full Name',
    companyName: 'Company Name',
    email: 'Email Address',
    emailHint: 'Email cannot be changed',
    saveChanges: 'Save Changes',
    saving: 'Saving...',
    profileSaved: 'Profile updated!',
    profileError: 'Error updating profile',
    preferences: 'Preferences',
    theme: 'Theme',
    themeHint: 'Choose your interface theme',
    light: 'Light',
    dark: 'Dark',
    notifications: 'Email Notifications',
    notificationsHint: 'Receive updates and alerts',
    language: 'Default Language',
    languageHint: 'Preferred interface language',
    savePrefs: 'Save Preferences',
    prefsSaved: 'Preferences saved!',
    prefsError: 'Error saving preferences',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm New Password',
    changeBtn: 'Change Password',
    changing: 'Changing...',
    passwordChanged: 'Password changed!',
    passwordMismatch: 'New passwords do not match',
    aiTitle: 'AI Model Settings',
    aiDesc: 'Choose which AI model powers all tools in the platform. You can use our default Gemini or connect your own API keys.',
    aiProvider: 'AI Provider',
    noKeyWarning: 'No API key configured for',
    addKeyLink: 'Add API Key',
    model: 'Model',
    testConnection: 'Test Connection',
    testing: 'Testing...',
    saveAi: 'Save AI Settings',
    aiSaved: 'AI settings saved!',
    aiError: 'Error saving AI settings',
    connectionSuccess: 'Connection Successful!',
    connectionFailed: 'Connection Failed',
    deleteTitle: 'Delete Account',
    deleteDesc: 'This will permanently delete your account, including all business data, documents, analyses and your AI assistant Emma. This cannot be undone.',
    deleteBtn: 'Delete Account',
    deleteConfirmTitle: 'Delete Account?',
    deleteConfirmMsg: 'Are you sure you want to delete your account? <strong>All data</strong> including your company profile, documents, analyses and your AI assistant <strong>Emma</strong> will be permanently deleted. This cannot be undone.',
    deleteTypePrompt: 'Type <strong>DELETE</strong> to confirm:',
    deleteWord: 'DELETE',
    cancel: 'Cancel',
    deleting: 'Deleting...',
    deleteForever: 'Delete Permanently',
    deleteError: 'Something went wrong',
  },
};

const Profile = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { lang } = useLanguage();
  const t = T[lang] || T.nl;
  const [profile, setProfile] = useState({ full_name: '', company_name: '' });
  const [preferences, setPreferences] = useState({ theme: 'light', email_notifications: true, default_language: 'en' });
  const [passwordChange, setPasswordChange] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [llmSettings, setLlmSettings] = useState({ provider: 'gemini', model: 'gemini-2.0-flash', configured_providers: [], has_platform_gemini: false });
  const [availableModels, setAvailableModels] = useState({});
  const [testingLlm, setTestingLlm] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => { fetchProfile(); fetchLlmSettings(); }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile/`, { headers: { Authorization: `Bearer ${token}` } });
      setProfile(response.data.user);
      setPreferences(response.data.preferences);
    } catch (error) { console.error('Error fetching profile:', error); }
    setLoading(false);
  };

  const fetchLlmSettings = async () => {
    try {
      const response = await axios.get(`${API}/profile/llm-settings`, { headers: { Authorization: `Bearer ${token}` } });
      setLlmSettings(response.data.settings);
      setAvailableModels(response.data.available_models);
    } catch (error) { console.error('Error fetching LLM settings:', error); }
  };

  const updateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/profile/update`, profile, { headers: { Authorization: `Bearer ${token}` } });
      alert(t.profileSaved);
    } catch { alert(t.profileError); }
    setSaving(false);
  };

  const updatePreferences = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile/preferences`, preferences, { headers: { Authorization: `Bearer ${token}` } });
      document.body.classList.toggle('dark-mode', preferences.theme === 'dark');
      alert(t.prefsSaved);
    } catch { alert(t.prefsError); }
    setSaving(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (passwordChange.new_password !== passwordChange.confirm_password) { alert(t.passwordMismatch); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/profile/change-password`, { current_password: passwordChange.current_password, new_password: passwordChange.new_password }, { headers: { Authorization: `Bearer ${token}` } });
      alert(t.passwordChanged);
      setPasswordChange({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) { alert(error.response?.data?.detail || t.profileError); }
    setSaving(false);
  };

  const updateLlmSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile/llm-settings`, { provider: llmSettings.provider, model: llmSettings.model }, { headers: { Authorization: `Bearer ${token}` } });
      alert(t.aiSaved);
    } catch (error) { alert(error.response?.data?.detail || t.aiError); }
    setSaving(false);
  };

  const testLlmConnection = async () => {
    setTestingLlm(true);
    setTestResult(null);
    try {
      const response = await axios.post(`${API}/profile/llm-settings/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setTestResult(response.data);
    } catch (error) { setTestResult({ status: 'error', error: error.response?.data?.detail || t.connectionFailed }); }
    setTestingLlm(false);
  };

  const handleProviderChange = (provider) => {
    const defaultModel = availableModels[provider]?.[0]?.id || '';
    setLlmSettings({ ...llmSettings, provider, model: defaultModel });
    setTestResult(null);
  };

  if (loading) return <div className="loading-screen"><div className="loader"></div><p>{t.loading}</p></div>;

  return (
    <div className="tool-page">
      <div className="tool-header">
        <h1 className="heading-1">{t.title}</h1>
      </div>

      <div className="profile-container">
        {/* Profile Information */}
        <div className="profile-section">
          <h2 className="heading-2">{t.profileInfo}</h2>
          <form onSubmit={updateProfile} className="profile-form">
            <div className="form-group">
              <label className="body-small">{t.fullName}</label>
              <div className="input-with-icon">
                <User size={18} />
                <input type="text" value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} className="form-input" />
              </div>
            </div>
            <div className="form-group">
              <label className="body-small">{t.companyName}</label>
              <div className="input-with-icon">
                <Building size={18} />
                <input type="text" value={profile.company_name} onChange={(e) => setProfile({ ...profile, company_name: e.target.value })} className="form-input" />
              </div>
            </div>
            <div className="form-group">
              <label className="body-small">{t.email}</label>
              <div className="input-with-icon">
                <Mail size={18} />
                <input type="email" value={profile.email} disabled className="form-input" style={{ opacity: 0.6, cursor: 'not-allowed' }} />
              </div>
              <p className="caption">{t.emailHint}</p>
            </div>
            <button type="submit" disabled={saving} className="btn-primary">
              <Save size={16} /> {saving ? t.saving : t.saveChanges}
            </button>
          </form>
        </div>

        {/* Preferences */}
        <div className="profile-section">
          <h2 className="heading-2">{t.preferences}</h2>
          <div className="preferences-grid">
            <div className="preference-item">
              <div className="preference-info">
                {preferences.theme === 'light' ? <Sun size={20} /> : <Moon size={20} />}
                <div>
                  <p className="body-medium" style={{ fontWeight: 500 }}>{t.theme}</p>
                  <p className="caption">{t.themeHint}</p>
                </div>
              </div>
              <select value={preferences.theme} onChange={(e) => setPreferences({ ...preferences, theme: e.target.value })} className="form-input" style={{ maxWidth: '150px' }}>
                <option value="light">{t.light}</option>
                <option value="dark">{t.dark}</option>
              </select>
            </div>
            <div className="preference-item">
              <div className="preference-info">
                <Bell size={20} />
                <div>
                  <p className="body-medium" style={{ fontWeight: 500 }}>{t.notifications}</p>
                  <p className="caption">{t.notificationsHint}</p>
                </div>
              </div>
              <label className="toggle-switch">
                <input type="checkbox" checked={preferences.email_notifications} onChange={(e) => setPreferences({ ...preferences, email_notifications: e.target.checked })} />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <div className="preference-item">
              <div className="preference-info">
                <Globe size={20} />
                <div>
                  <p className="body-medium" style={{ fontWeight: 500 }}>{t.language}</p>
                  <p className="caption">{t.languageHint}</p>
                </div>
              </div>
              <select value={preferences.default_language} onChange={(e) => setPreferences({ ...preferences, default_language: e.target.value })} className="form-input" style={{ maxWidth: '150px' }}>
                <option value="en">English</option>
                <option value="nl">Nederlands</option>
              </select>
            </div>
          </div>
          <button onClick={updatePreferences} disabled={saving} className="btn-primary">
            <Save size={16} /> {saving ? t.saving : t.savePrefs}
          </button>
        </div>

        {/* Change Password */}
        <div className="profile-section">
          <h2 className="heading-2">{t.changePassword}</h2>
          <form onSubmit={changePassword} className="profile-form">
            <div className="form-group">
              <label className="body-small">{t.currentPassword}</label>
              <div className="input-with-icon"><Lock size={18} /><input type="password" value={passwordChange.current_password} onChange={(e) => setPasswordChange({ ...passwordChange, current_password: e.target.value })} className="form-input" required /></div>
            </div>
            <div className="form-group">
              <label className="body-small">{t.newPassword}</label>
              <div className="input-with-icon"><Lock size={18} /><input type="password" value={passwordChange.new_password} onChange={(e) => setPasswordChange({ ...passwordChange, new_password: e.target.value })} className="form-input" required minLength="6" /></div>
            </div>
            <div className="form-group">
              <label className="body-small">{t.confirmPassword}</label>
              <div className="input-with-icon"><Lock size={18} /><input type="password" value={passwordChange.confirm_password} onChange={(e) => setPasswordChange({ ...passwordChange, confirm_password: e.target.value })} className="form-input" required /></div>
            </div>
            <button type="submit" disabled={saving} className="btn-primary">
              <Lock size={16} /> {saving ? t.changing : t.changeBtn}
            </button>
          </form>
        </div>

        {/* AI / LLM Settings */}
        <div className="profile-section" data-testid="llm-settings">
          <h2 className="heading-2">
            <Bot size={20} style={{ display: 'inline', marginRight: 8, verticalAlign: 'middle' }} />
            {t.aiTitle}
          </h2>
          <p className="body-small text-secondary" style={{ marginBottom: '1.5rem' }}>{t.aiDesc}</p>

          <div className="form-group">
            <label className="body-small" style={{ fontWeight: 600 }}>{t.aiProvider}</label>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              {['gemini', 'openai', 'anthropic'].map((provider) => {
                const isConfigured = llmSettings.configured_providers?.includes(provider);
                const isPlatformGemini = provider === 'gemini' && llmSettings.has_platform_gemini;
                return (
                  <button key={provider} type="button"
                    className={`compass-nav-item ${llmSettings.provider === provider ? 'active' : ''}`}
                    onClick={() => handleProviderChange(provider)}
                    style={{ flex: 1, justifyContent: 'center', minWidth: '120px', position: 'relative' }}
                    data-testid={`provider-${provider}`}>
                    {provider === 'gemini' && 'Google Gemini'}
                    {provider === 'openai' && 'OpenAI GPT'}
                    {provider === 'anthropic' && 'Anthropic Claude'}
                    {(isConfigured || isPlatformGemini) && <CheckCircle size={12} style={{ marginLeft: 6, color: 'var(--accent-green)' }} />}
                  </button>
                );
              })}
            </div>
            {!llmSettings.configured_providers?.includes(llmSettings.provider) &&
             !(llmSettings.provider === 'gemini' && llmSettings.has_platform_gemini) && (
              <p className="caption" style={{ color: 'var(--accent-orange)', marginTop: '0.5rem' }}>
                <AlertCircle size={12} style={{ display: 'inline', marginRight: 4 }} />
                {t.noKeyWarning} {llmSettings.provider}.
                <a href="/portal/api-keys" style={{ marginLeft: 4, color: 'var(--accent-blue)' }}>{t.addKeyLink}</a>
              </p>
            )}
          </div>

          <div className="form-group" style={{ marginTop: '1rem' }}>
            <label className="body-small" style={{ fontWeight: 600 }}>{t.model}</label>
            <select value={llmSettings.model} onChange={(e) => setLlmSettings({ ...llmSettings, model: e.target.value })} className="form-input" style={{ marginTop: '0.5rem' }} data-testid="model-select">
              {availableModels[llmSettings.provider]?.map((model) => (
                <option key={model.id} value={model.id}>{model.name} — {model.description}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
            <button onClick={testLlmConnection} disabled={testingLlm} className="btn-secondary" data-testid="test-llm-btn">
              {testingLlm ? <><Loader size={16} className="spin" /> {t.testing}</> : <><Key size={16} /> {t.testConnection}</>}
            </button>
            <button onClick={updateLlmSettings} disabled={saving} className="btn-primary" data-testid="save-llm-btn">
              <Save size={16} /> {saving ? t.saving : t.saveAi}
            </button>
          </div>

          {testResult && (
            <div className={`llm-test-result ${testResult.status === 'success' ? 'success' : 'error'}`}
              style={{ marginTop: '1rem', padding: '1rem', borderRadius: '8px',
                background: testResult.status === 'success' ? 'var(--accent-green-100)' : 'var(--accent-pink-100)',
                border: `1px solid ${testResult.status === 'success' ? 'var(--accent-green)' : 'var(--accent-pink)'}` }}
              data-testid="llm-test-result">
              {testResult.status === 'success' ? (
                <>
                  <p className="body-small" style={{ fontWeight: 600, color: 'var(--accent-green)' }}>
                    <CheckCircle size={14} style={{ display: 'inline', marginRight: 6 }} /> {t.connectionSuccess}
                  </p>
                  <p className="caption" style={{ marginTop: '0.5rem' }}>Provider: {testResult.provider} | Model: {testResult.model}</p>
                  <p className="body-small" style={{ marginTop: '0.5rem', fontStyle: 'italic' }}>"{testResult.response}"</p>
                </>
              ) : (
                <>
                  <p className="body-small" style={{ fontWeight: 600, color: 'var(--accent-pink)' }}>
                    <AlertCircle size={14} style={{ display: 'inline', marginRight: 6 }} /> {t.connectionFailed}
                  </p>
                  <p className="caption" style={{ marginTop: '0.5rem' }}>{testResult.error}</p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Delete Account */}
        <div className="profile-section" style={{ borderTop: '2px solid #fee2e2', paddingTop: '2rem' }}>
          <h2 className="heading-2" style={{ color: '#dc2626' }}>
            <Trash2 size={20} style={{ display: 'inline', marginRight: 8, verticalAlign: 'middle' }} />
            {t.deleteTitle}
          </h2>
          <p className="body-small text-secondary" style={{ marginBottom: '1rem' }}>{t.deleteDesc}</p>
          <button onClick={() => setShowDeleteModal(true)} className="btn-secondary" data-testid="delete-account-btn" style={{ color: '#dc2626', borderColor: '#fca5a5' }}>
            <Trash2 size={16} /> {t.deleteBtn}
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} data-testid="delete-modal">
          <div style={{ background: '#0E1620', borderRadius: 16, padding: '2rem', maxWidth: 480, width: '90vw', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem' }}>
              <div style={{ background: '#16202C', borderRadius: '50%', padding: 10 }}>
                <AlertTriangle size={24} style={{ color: '#dc2626' }} />
              </div>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#F4F7FB' }}>{t.deleteConfirmTitle}</h3>
            </div>
            <p style={{ fontSize: '0.925rem', lineHeight: 1.6, color: '#555', marginBottom: '1.25rem' }} dangerouslySetInnerHTML={{ __html: t.deleteConfirmMsg }} />
            <p style={{ fontSize: '0.85rem', color: '#777', marginBottom: '0.75rem' }} dangerouslySetInnerHTML={{ __html: t.deleteTypePrompt }} />
            <input type="text" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder={t.deleteWord} className="form-input" data-testid="delete-confirm-input" style={{ marginBottom: '1.25rem' }} />
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }} className="btn-secondary" data-testid="delete-cancel-btn">{t.cancel}</button>
              <button disabled={deleteConfirmText !== t.deleteWord || deleting} data-testid="delete-confirm-btn"
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await axios.delete(`${API}/auth/account`, { headers: { Authorization: `Bearer ${token}` } });
                    localStorage.clear();
                    window.location.href = '/';
                  } catch (err) { alert(err.response?.data?.detail || t.deleteError); setDeleting(false); }
                }}
                style={{
                  padding: '0.6rem 1.25rem', borderRadius: 8, border: 'none',
                  background: deleteConfirmText === t.deleteWord ? '#dc2626' : '#16202C',
                  color: deleteConfirmText === t.deleteWord ? '#fff' : '#999',
                  cursor: deleteConfirmText === t.deleteWord ? 'pointer' : 'not-allowed',
                  fontWeight: 600, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 6,
                }}>
                {deleting ? <Loader size={16} className="spin" /> : <Trash2 size={16} />}
                {deleting ? t.deleting : t.deleteForever}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
